import './Modal.module.less';

import ModalComponent from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import { clsx } from 'clsx';
import { type CSSProperties, type ReactNode, useEffect, useRef } from 'react';

type Style = {
  compact?: boolean;
  maxWidth?: number | 'none';
  fullScreen?: boolean;
  fullHeight?: boolean;
};

interface Props extends Style {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  onOpen?: () => void;
  className?: string;
  styles?: CSSProperties;
}

export const Modal = ({
  open,
  onClose,
  children,
  maxWidth = 'none',
  fullScreen = false,
  fullHeight = false,
  className = '',
  styles = {},
  onOpen,
}: Props) => {
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      // Store the currently focused element when modal opens
      previousActiveElementRef.current = document.activeElement as HTMLElement;
      if (onOpen) {
        onOpen();
      }
    } else {
      // Restore focus when modal closes
      if (previousActiveElementRef.current) {
        // Use setTimeout to ensure the modal is fully closed before restoring focus
        setTimeout(() => {
          previousActiveElementRef.current?.focus();
        }, 0);
      }
    }
  }, [open, onOpen]);

  return (
    <ModalComponent open={open} onClose={onClose} className={'Modal'}>
      <Paper
        className={clsx('ModalPaper', className)}
        style={{
          maxWidth,
          width: fullScreen ? '100%' : 'auto',
          height: fullScreen || fullHeight ? '100%' : 'auto',
          ...styles,
        }}
      >
        {children}
      </Paper>
    </ModalComponent>
  );
};
